/**
 * A task description written over MCP keeps the task's own images (card
 * "MCP: board_task_update loses images when an agent rewrites a description",
 * 04.10.2026).
 *
 * `board_read` never hands out an image's decryption key (card MCP 1,
 * `mediaKeys.js`): in markdown/text an encrypted image is a line of its own,
 * "🖼 Image: name" / "▶ Video: name", and in html it is an `<img>` without
 * `data-enc-key`. An agent that reads a task, edits the words and writes them
 * back used to drop every image — the line became text, and the keyless tag was
 * refused by the sanitizer.
 *
 * So, on the way in:
 *  - every key the agent sends is stripped first (`data-enc-key` / `data-enc`).
 *    The only keys that can reach the stored HTML are the ones THIS task
 *    already holds — an image is never invented and never moved between tasks;
 *  - markdown: a line that is exactly the line `board_read` showed for one of
 *    the task's images becomes that image again (its stored tag, key included).
 *    Matched by name in order: the first unused image with that line, so a
 *    reordered description keeps its images and two images of the same name
 *    are taken in their old order. A line naming an image the task does not
 *    have, a second line for an image already placed, and any line inside a
 *    code fence stay plain text. An image whose line is gone is removed;
 *  - html: an `<img>` whose `src` (or pending-upload ref) is one of the task's
 *    images gets its key back (`restoreMediaKeys`, as `document_write` does).
 *
 * The editor's block snapshot (`descriptionLexical`, team boards) is cleared
 * on a description write: see `descriptionPatch` below. Shared by MCP
 * (handlers/boardWrites.js) and the in-app AI (hooks/useAiExtensionActions.js).
 */
import { markdownToHtml } from '../utils/markdownToHtml';
import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';
import { renderBody } from './markdown';
import { restoreMediaKeys, stripMediaKeyAttrs } from './mediaKeys';

export const DESCRIPTION_FORMATS = Object.freeze(['markdown', 'html']);

/** The label shape `mediaLabel` writes (and nothing else is matched). */
const LABEL_RE = /^(?:🖼 Image|▶ Video): \S/u;

// Private-use sentinels for a placed image: they pass through markdownToHtml
// untouched and never occur in real text — any the agent sends are removed
// first, so an index cannot be smuggled in to bring a removed image back.
const OPEN = String.fromCharCode(0xe010);
const CLOSE = String.fromCharCode(0xe011);
const SENTINELS_RE = new RegExp(`[${OPEN}${CLOSE}]`, 'g');
const PLACED_RE = new RegExp(`(?:<p>\\s*)?${OPEN}(\\d+)${CLOSE}(?:\\s*</p>)?`, 'g');

/** Every image of the stored description, in order, with the lines `board_read` showed for it. */
export function taskImages(currentHtml) {
  return [...String(currentHtml ?? '').matchAll(/<img\b[^>]*>/gi)].map((m) => ({
    tag: m[0],
    lines: new Set([renderBody(m[0], 'markdown').trim(), renderBody(m[0], 'text').trim()]),
  }));
}

/**
 * The markdown with every image line that names one of `images` replaced by a
 * sentinel paragraph of its own; returns the text and the indices placed.
 */
function placeImageLines(markdown, images) {
  const used = new Set();
  let fence = false;
  const out = [];
  for (const line of String(markdown).replace(/\r\n?/g, '\n').split('\n')) {
    // markdownToHtml's own fence rules, so a line it shows as code stays code.
    if (fence ? /^```\s*$/.test(line) : /^```(\w*)\s*$/.test(line)) fence = !fence;
    const label = line.trim();
    const at = !fence && LABEL_RE.test(label)
      ? images.findIndex((img, i) => !used.has(i) && img.lines.has(label))
      : -1;
    if (at < 0) {
      out.push(line);
    } else {
      used.add(at);
      out.push('', `${OPEN}${at}${CLOSE}`, '');
    }
  }
  return { text: out.join('\n'), used };
}

/**
 * The sanitised HTML to store for a description an agent wrote, keeping the
 * images the task already has (see the header).
 *
 * `added` are encrypted media tags for NEW files (`document_write`
 * `attachments`, MCP 10): built and checked by `attachmentTags`, never taken
 * from the agent's text. They join the pool, so a "🖼 Image: name" line
 * (markdown) or an `<img src>` (html) places one where the agent wants it;
 * any not placed is appended at the end — an attachment is never dropped.
 *
 * @param {string} text        the agent's description
 * @param {string} format      'markdown' (default) or 'html'
 * @param {string} currentHtml the task's stored descriptionHtml ('' for a new task)
 * @param {string[]} added     media tags of new attachments
 */
export function descriptionHtmlFor(text, format, currentHtml = '', added = []) {
  const input = stripMediaKeyAttrs(String(text ?? '')).replace(SENTINELS_RE, '');
  if (format === 'html') {
    // An <img> naming a new attachment becomes its full checked tag (type,
    // size and name included, so a video stays a video), once.
    const bySrc = new Map(added.map((tag) => [srcOf(tag), tag]));
    const placed = new Set();
    const withAdded = input.replace(/<img\b[^>]*>/gi, (whole) => {
      const src = srcOf(whole);
      if (!bySrc.has(src)) return whole;
      if (placed.has(src)) return '';
      placed.add(src);
      return bySrc.get(src);
    });
    const rest = added.filter((tag) => !placed.has(srcOf(tag)));
    return sanitizeRichTextHtml(restoreMediaKeys(withAdded, currentHtml) + rest.join(''));
  }
  // New files first: a line naming one places the file the agent just
  // uploaded, even when an older image has the same name.
  const fresh = taskImages(added.join(''));
  const images = [...fresh, ...taskImages(currentHtml)];
  if (!images.length) return sanitizeRichTextHtml(markdownToHtml(input));
  const { text: placedText, used } = placeImageLines(input, images);
  const html = markdownToHtml(placedText).replace(PLACED_RE, (_m, i) => images[Number(i)]?.tag || '');
  const rest = fresh.filter((_img, i) => !used.has(i)).map((img) => img.tag);
  return sanitizeRichTextHtml(html + rest.join(''));
}

/** An `<img>` tag's `src`, in any quoting, `&amp;` read as `&`. */
const srcOf = (tag) => {
  const m = /\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(tag);
  return (m ? m[1] ?? m[2] ?? m[3] : '').replace(/&amp;/g, '&');
};

/**
 * A description an agent wrote → the fields to store. Markdown (default) or
 * html in, sanitised HTML out, the task's own images kept (`descriptionHtmlFor`).
 *
 * The editor's block snapshot (`descriptionLexical`, team boards) describes the
 * text just replaced, and the task modal opens from it whenever it is there —
 * carried forward it would show the OLD text and save it back on the first
 * keystroke. It is cleared, as `document_write` clears `bodyLexical`: the
 * modal then opens the task from its HTML (the legacy path every task without
 * a snapshot takes) and its next save writes a fresh snapshot (dual-write).
 *
 * @param {string} text
 * @param {string} format  'markdown' (default) or 'html'
 * @param {object|null} task the stored task (row or board task), null for a new one
 */
export function descriptionPatch(text, format, task = null) {
  const patch = { descriptionHtml: descriptionHtmlFor(text, format, task?.descriptionHtml || '') };
  if (task?.descriptionLexical) patch.descriptionLexical = null;
  // Its compact stored form (taskDescriptionCodec.js) on a raw row, likewise.
  if (task?.descriptionBlocks) patch.descriptionBlocks = null;
  return patch;
}
